import * as THREE from 'three';
import { basic, emis, smoke, std } from '../materials.ts';
import { G, M, Ribbon, V, eyesGlow, glowSprite, limb } from '../prims.ts';
import { LIGHT_K, begin, bonesOf, done, joint, pointLight, type BaseOpts, type Model } from './types.ts';

export interface DragonOpts extends BaseOpts {
  /** On the wing: wings spread and beating, legs tucked, tail streaming behind. Default: landed. */
  flying?: boolean;
}

/** Frames baked for the dragon: only its boil (fire, embers, smoke); the wing beat and breathing are clips. */
export const DRAGON_FRAMES = 8;

/**
 * El Dragón de la Quemadura (el Último Sueño, docs/LORE.md): the titan's oldest nightmare, made of what
 * it left. A body of black volcanic glass like the Burn, split by cracks with embers and violet fire
 * inside; wings of smoke that boil and re-form every frame like any nightmare's; a crown of fire.
 * About 4.6 long and 2.9 tall landed, 5.6 across the wings in flight (a hero is 1.8).
 *
 * Rigged (anim/creatures/dragon.ts): a trunk with chest and hips, three neck bones, head and jaw, five tail
 * bones, four legs (thigh and shin) and each wing in three (arm, forearm, hand). At rest it stands as the
 * first frame of the old baked beat; the clips do all the moving, `tick` only boils.
 */
export const dragon = (o?: DragonOpts): Model => {
  const fly = o?.flying === true;
  const c = begin(o, 1.9, 44);
  const { g, rng } = c;
  const R = (): number => rng() - 0.5;
  // The whole beast sits in its own group under `g` (the gallery lifts `g` in flight).
  const beast = G(g);
  if (fly && o?.base) g.position.y = 1.1;

  // Black glass, faceted, with a deep violet light of its own so it reads against the Burn's dark (never a silhouette
  // at the game's camera); a second, bluer glass for the scales that catch the light.
  const glass = std('#2a2536', { roughness: 0.12, metalness: 0.3, flatShading: true, bumpScale: 0, emissive: '#2a1240', emissiveIntensity: 0.55 });
  const scaleM = std('#3c3854', { roughness: 0.05, metalness: 0.45, flatShading: true, bumpScale: 0, emissive: '#3a2468', emissiveIntensity: 0.5 });
  const dark = std('#15121c', { roughness: 0.3, metalness: 0.2, flatShading: true, bumpScale: 0, emissive: '#1a0c28', emissiveIntensity: 0.4 });
  const ember = emis('#ffc48a', '#ff4a12', 2.2);
  const violet = emis('#e6c4ff', '#9b4dff', 2.0);
  const heartM = emis('#fff0d0', '#ff7a2a', 2.6);
  const teethM = std('#2a2430', { roughness: 0.15, metalness: 0.5, flatShading: true, bumpScale: 0 });

  /** A faceted lump of glass. */
  const shard = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, r: number, sx = 1, sy = 1, sz = 1, detail = 1): THREE.Mesh => {
    const m = M(new THREE.IcosahedronGeometry(r, detail), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    m.rotation.set(R() * 0.6, R() * 0.6, R() * 0.6);
    return m;
  };
  /** The points of a jagged seam: doubled, with the midpoints jittered. */
  const jitter = (pts: THREE.Vector3[]): THREE.Vector3[] => {
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(p);
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V(R() * 0.07, R() * 0.07, R() * 0.07)));
    });
    return jit;
  };
  const tube = (parent: THREE.Object3D, jit: THREE.Vector3[], r: number, mat: THREE.Material): void => {
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(jit, false, 'catmullrom', 0.1), jit.length * 2, r, 3), mat, parent);
  };
  /** A jagged seam of fire: doubled, jittered points joined by short runs. */
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r: number, mat: THREE.Material): void => tube(parent, jitter(pts), r * 1.5, mat);
  /** The same seam over several bones: run i (from jittered point cuts[i] to the next cut) rides parts[i]. */
  const crackOver = (parts: THREE.Object3D[], cuts: number[], pts: THREE.Vector3[], r: number, mat: THREE.Material): void => {
    const jit = jitter(pts);
    parts.forEach((p, i) => tube(p, jit.slice(cuts[i], (cuts[i + 1] ?? jit.length - 1) + 1), r, mat));
  };
  /** A seam running over an ellipsoid (center, radii) from (u, v) with steps (du, dv), in radians. */
  const seam = (parent: THREE.Object3D, ctr: THREE.Vector3, rad: THREE.Vector3, u: number, v: number, du: number, dv: number, n: number, mat: THREE.Material, r = 0.018): void => {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i < n; i++) {
      const uu = u + du * i + R() * 0.15, vv = v + dv * i + R() * 0.12;
      pts.push(V(ctr.x + rad.x * Math.cos(vv) * Math.sin(uu), ctr.y + rad.y * Math.sin(vv), ctr.z + rad.z * Math.cos(vv) * Math.cos(uu)));
    }
    crack(parent, pts, r, mat);
    // The fire inside shows through the split.
    for (let i = 1; i < n; i += 2) glowSprite(parent, mat === violet ? '#b06bff' : '#ff6a1f', 0.42, pts[i]!, 0.62);
  };
  /** A blade of glass sticking out (a scale, a spine), pointing along `dir`. */
  const blade = (parent: THREE.Object3D, mat: THREE.Material, at: THREE.Vector3, dir: THREE.Vector3, len: number, w: number): THREE.Mesh => {
    const m = M(new THREE.ConeGeometry(w, len, 4), mat, parent);
    m.position.copy(at).addScaledVector(dir.clone().normalize(), len * 0.45);
    m.quaternion.setFromUnitVectors(V(0, 1, 0), dir.clone().normalize());
    m.scale.z = 0.45;
    return m;
  };

  /* ---------- body ---------- */
  const frame = G(beast, 0, fly ? 0.25 : 0.12, 0);
  // Landed it rears a little, chest high; in flight it lies along the air.
  frame.rotation.x = fly ? -0.08 : -0.14;
  const chest = V(0, 1.22, 0.55), mid = V(0, 1.08, -0.15), hips = V(0, 1.0, -0.85);
  // The trunk turns about its middle; the chest (neck, wings, forelegs) and the hips (tail, hind legs) hang off it.
  const trunkJ = joint(frame, mid, 'body');
  const chestJ = joint(trunkJ.abs, V(0, 1.12, 0.05), 'chest');
  const hipsJ = joint(trunkJ.abs, V(0, 1.05, -0.4), 'hips');
  const body = trunkJ.abs, front = chestJ.abs, back = hipsJ.abs;
  /** The part of the trunk a point along it belongs to. */
  const along = (z: number): THREE.Object3D => (z > 0.3 ? front : z < -0.55 ? back : body);
  // A heavy beast: a deep, broad chest, a thick barrel of a body and wide haunches.
  shard(front, glass, chest.x, chest.y + 0.05, chest.z, 0.7, 1.35, 1.12, 1.2);
  shard(body, glass, mid.x, mid.y, mid.z, 0.64, 1.3, 1.0, 1.3);
  shard(back, glass, hips.x, hips.y, hips.z, 0.52, 1.25, 0.95, 1.15);
  // Shoulders like boulders where the wings grow out.
  for (const k of [-1, 1]) shard(front, glass, k * 0.55, 1.5, 0.45, 0.4, 1.1, 0.9, 1.15);
  // Belly: darker glass, hanging low.
  shard(body, dark, 0, 0.74, 0.15, 0.5, 1.5, 0.66, 1.7);
  // The heart, glimpsed through a rift in the chest.
  const heart = M(new THREE.IcosahedronGeometry(0.2, 1), heartM, front);
  heart.position.set(0, 1.12, 1.36);
  const heartGlow = glowSprite(front, '#ff6a1f', 1.5, V(0, 1.12, 1.46), 0.6, true);
  glowSprite(body, '#9b4dff', 2.4, V(0, 1.15, 0.2), 0.18);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    blade(front, scaleM, V(Math.cos(a) * 0.24, 1.12 + Math.sin(a) * 0.24, 1.3), V(Math.cos(a), Math.sin(a), 0.5), 0.26, 0.07);
  }
  // Seams of ember and violet fire over chest, flank and haunch.
  const BODY: [THREE.Vector3, THREE.Vector3][] = [
    [chest, V(0.9, 0.75, 0.8)],
    [mid, V(0.8, 0.6, 0.78)],
    [hips, V(0.62, 0.47, 0.57)],
  ];
  for (const [ctr, rad] of BODY) {
    for (let i = 0; i < 6; i++) {
      const u = (i / 6) * Math.PI * 2 + R();
      seam(along(ctr.z), ctr, rad, u, -0.6 + R() * 0.4, 0.22 + R() * 0.15, 0.28, 5, i % 3 === 0 ? violet : ember, 0.028);
    }
  }
  // The scales: plates of glass along the flanks, catching the light.
  for (let i = 0; i < 18; i++) {
    const k = i % 2 ? 1 : -1;
    const t = Math.floor(i / 2) / 8;
    const z = 0.95 - t * 1.9;
    const y = 1.1 + Math.sin(t * Math.PI) * 0.1 + R() * 0.12;
    const at = V(k * (0.62 + Math.sin(t * Math.PI) * 0.14), y, z);
    blade(along(z), scaleM, at, V(k, 0.55 + R() * 0.4, -0.5), 0.3 + rng() * 0.12, 0.09);
  }
  // Spine: a ridge of glass blades, tallest over the shoulders.
  for (let i = 0; i < 9; i++) {
    const t = i / 8;
    const z = 0.85 - t * 1.85;
    const y = 1.98 - t * 0.5 - Math.abs(t - 0.2) * 0.15;
    blade(along(z), i % 3 === 1 ? violet : glass, V(0, y, z), V(0, 1, -0.45), 0.42 - t * 0.18, 0.07);
  }

  /* ---------- neck and head ---------- */
  const neckPts = fly
    ? [V(0, 1.45, 0.95), V(0, 1.6, 1.35), V(0, 1.68, 1.8), V(0, 1.7, 2.25)]
    : [V(0, 1.45, 0.95), V(0, 1.95, 1.2), V(0, 2.4, 1.35), V(0, 2.65, 1.7)];
  const neck = new THREE.CatmullRomCurve3(neckPts);
  // Three bones down the neck, each carrying the vertebrae past it: the whip of a long neck.
  const n1 = joint(front, neck.getPoint(0), 'neck1');
  const n2 = joint(n1.abs, neck.getPoint(0.36), 'neck2');
  const n3 = joint(n2.abs, neck.getPoint(0.7), 'neck3');
  const vert = (t: number): THREE.Object3D => (t < 0.36 ? n1.abs : t < 0.7 ? n2.abs : n3.abs);
  for (let i = 0; i < 8; i++) {
    const t = i / 7;
    const p = neck.getPoint(t);
    shard(vert(t), glass, p.x, p.y, p.z, 0.46 - t * 0.2, 1.15, 1.0, 1.1);
    if (i % 2 === 0) blade(vert(t), glass, p.clone().add(V(0, 0.22 - t * 0.06, 0)), V(0, 1, -0.6), 0.28 - t * 0.08, 0.05);
  }
  // Fire in the throat, glowing through the underside of the neck.
  crackOver([n1.abs, n2.abs, n3.abs], [0, 6, 11], neck.getPoints(8).map((p, i) => p.clone().add(V(R() * 0.04, -0.2 + i * 0.012, 0.04))), 0.03, ember);

  const end = neck.getPoint(1);
  const headAt = V(end.x, end.y + 0.05, end.z + 0.12);
  const headJ = joint(n3.abs, headAt, 'head');
  const head = G(headJ.abs, headAt.x, headAt.y, headAt.z);
  head.rotation.x = fly ? 0.12 : 0.3;
  shard(head, glass, 0, 0, 0, 0.27, 0.95, 0.72, 1.25);
  const snout = M(new THREE.ConeGeometry(0.17, 0.62, 5), glass, head);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, 0.0, 0.48);
  snout.scale.set(1, 1, 0.7);
  for (const k of [-1, 1]) {
    // Brow ridges, nostrils smoking with ember.
    blade(head, scaleM, V(k * 0.12, 0.12, 0.12), V(k * 0.3, 0.6, -1), 0.3, 0.06);
    M(new THREE.SphereGeometry(0.025, 5, 4), ember, head).position.set(k * 0.06, 0.04, 0.76);
  }
  // The jaw hangs from its hinge (a bone: the clips open it); fire glows inside the mouth.
  const jaw = joint(head, V(0, -0.1, 0), 'jaw').p;
  jaw.rotation.x = fly ? 0.15 : 0.08;
  const lower = M(new THREE.ConeGeometry(0.13, 0.55, 5), dark, jaw);
  lower.rotation.x = Math.PI / 2;
  lower.position.set(0, -0.04, 0.38);
  lower.scale.set(1, 1, 0.55);
  for (let i = 0; i < 5; i++) {
    for (const k of [-1, 1]) {
      const tt = M(new THREE.ConeGeometry(0.02, 0.08, 3), teethM, i < 3 ? head : jaw);
      tt.position.set(k * (0.1 - i * 0.012), i < 3 ? -0.07 : 0.0, 0.25 + i * 0.09);
      if (i < 3) tt.rotation.x = Math.PI;
    }
  }
  const mouth = M(new THREE.SphereGeometry(0.1, 6, 5), ember, head);
  mouth.position.set(0, -0.08, 0.32);
  mouth.scale.set(1, 0.4, 2);
  const mouthGlow = glowSprite(head, '#ff7a2a', 0.7, V(0, -0.1, 0.55), 0.5, true);
  eyesGlow(head, '#ffb36a', 0.06, 0.25, 0.13, 0.03);
  // Horns swept back, and a crown of fire between them: ember and violet flames on a ring of glass spikes.
  for (const k of [-1, 1]) {
    const pts = [V(k * 0.14, 0.12, -0.12), V(k * 0.24, 0.24, -0.4), V(k * 0.28, 0.36, -0.7), V(k * 0.22, 0.52, -0.9)];
    for (let i = 0; i < 3; i++) limb(head, pts[i]!, pts[i + 1]!, 0.07 - i * 0.02, 0.05 - i * 0.02, glass, 5);
    limb(head, V(k * 0.2, 0.02, -0.1), V(k * 0.42, -0.02, -0.35), 0.04, 0.008, scaleM, 4);
  }
  const crown = G(head, 0, 0.2, -0.12);
  crown.rotation.x = -0.25;
  // Tongues of fire, ember and violet, licking up between the spikes: they flicker every frame.
  const fireE = emis('#ff9a5a', '#ff3a10', 2.2, { transparent: true, opacity: 0.88, depthWrite: false });
  const fireV = emis('#dcb4ff', '#8b3dff', 2.2, { transparent: true, opacity: 0.85, depthWrite: false });
  const fireCore = emis('#fff0d8', '#ff8a3a', 2.6, { transparent: true, opacity: 0.9, depthWrite: false });
  const tongues: { m: THREE.Mesh; h: number }[] = [];
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const at = V(Math.cos(a) * 0.2, 0, Math.sin(a) * 0.2);
    if (i % 3 !== 2) blade(crown, i % 2 ? scaleM : glass, at, V(Math.cos(a) * 0.35, 1, Math.sin(a) * 0.35), 0.24 + (i % 2) * 0.1, 0.035);
    const h = 0.32 + (i % 3) * 0.12;
    const tg = M(new THREE.ConeGeometry(0.06, h, 5), i % 3 === 1 ? fireV : fireE, crown);
    tg.position.set(Math.cos(a) * 0.17, h / 2, Math.sin(a) * 0.17);
    tg.rotation.set(Math.sin(a) * 0.3, 0, -Math.cos(a) * 0.3);
    tongues.push({ m: tg, h });
  }
  const coreF = M(new THREE.ConeGeometry(0.09, 0.4, 6), fireCore, crown);
  coreF.position.y = 0.2;
  tongues.push({ m: coreF, h: 0.4 });
  glowSprite(crown, '#ff5a1f', 0.9, V(0, 0.3, 0), 0.4);
  glowSprite(crown, '#9b4dff', 1.3, V(0, 0.25, 0), 0.2);

  /* ---------- legs ---------- */
  const legBones: Record<string, THREE.Object3D> = {};
  const leg = (k: number, fore: boolean): void => {
    const hip = fore ? V(k * 0.6, 1.05, 0.6) : V(k * 0.58, 0.95, -0.85);
    // Landed: planted wide; flying: pulled up under the belly, claws trailing.
    const knee = fly ? (fore ? V(k * 0.12, -0.25, 0.25) : V(k * 0.12, -0.3, -0.15)) : fore ? V(k * 0.18, -0.6, 0.02) : V(k * 0.2, -0.36, -0.42);
    const foot = fly ? (fore ? V(k * 0.08, -0.35, -0.1) : V(k * 0.06, -0.35, -0.65)) : fore ? V(k * 0.24, -1.22, 0.12) : V(k * 0.22, -0.88, -0.12);
    // Thigh and shin: each a bone, its parts placed in the leg's own frame (origin at the hip).
    const side = k < 0 ? 'L' : 'R', end = fore ? 'F' : 'B';
    const thighJ = joint(fore ? front : back, hip, `leg${end}${side}`);
    const shinJ = joint(thighJ.abs, hip.clone().add(knee), `shin${end}${side}`);
    legBones[`leg${end}${side}`] = thighJ.p;
    legBones[`shin${end}${side}`] = shinJ.p;
    const up = G(thighJ.abs, hip.x, hip.y, hip.z);
    const lo = G(shinJ.abs, hip.x, hip.y, hip.z);
    shard(up, glass, 0, 0, 0, fore ? 0.36 : 0.42, 1, 1.15, 1);
    limb(up, V(0, 0, 0), knee, fore ? 0.24 : 0.28, 0.18, glass, 6);
    limb(lo, knee, foot, 0.17, 0.12, glass, 6);
    shard(lo, glass, knee.x, knee.y, knee.z, 0.19);
    crackOver([up, lo], [0, 2], [V(k * 0.08, -0.05, 0.14), knee.clone().add(V(k * 0.07, 0.05, 0.08)), foot.clone().lerp(knee, 0.4).add(V(k * 0.05, 0, 0.06))], 0.014, ember);
    const ft = shard(lo, dark, foot.x, foot.y + 0.04, foot.z + 0.05, 0.13, 1.1, 0.55, 1.4, 0);
    ft.rotation.set(0, 0, 0);
    for (let t = -1; t <= 1; t++) {
      const cl = M(new THREE.ConeGeometry(0.03, 0.2, 4), scaleM, lo);
      cl.position.set(foot.x + t * 0.07, foot.y + 0.02, foot.z + 0.2);
      cl.rotation.x = fly ? Math.PI * 0.9 : Math.PI / 2 + 0.35;
    }
  };
  for (const k of [-1, 1]) {
    leg(k, true);
    leg(k, false);
  }

  /* ---------- tail ---------- */
  const tailPts = fly
    ? [V(0, 1.0, -1.15), V(0, 0.95, -1.9), V(0.1, 0.9, -2.6), V(0, 0.85, -3.3)]
    : [V(0, 0.95, -1.15), V(0.15, 0.55, -1.8), V(0.55, 0.18, -2.35), V(1.15, 0.1, -2.5), V(1.6, 0.12, -2.2)];
  const tailC = new THREE.CatmullRomCurve3(tailPts);
  // Five bones down the tail, each a fifth of it: a chain for the whip and the S-curve.
  const tailJ: { p: THREE.Group; abs: THREE.Group }[] = [];
  for (let j = 0; j < 5; j++) tailJ.push(joint(j ? tailJ[j - 1]!.abs : back, tailC.getPoint(j / 5), `tail${j + 1}`));
  const tail: THREE.Object3D[] = [];
  const N = 13;
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    const p = tailC.getPoint(t);
    const seg = G(tailJ[Math.min(4, Math.floor(t * 5 + 1e-6))]!.abs, p.x, p.y, p.z);
    shard(seg, glass, 0, 0, 0, 0.4 - t * 0.3, 1.1, 0.9, 1.25);
    if (i % 2 === 0 && i < N - 1) blade(seg, i % 4 === 0 ? violet : glass, V(0, 0.2 - t * 0.14, 0), V(0, 1, -0.5), 0.26 - t * 0.14, 0.045);
    if (i % 3 === 1) crack(seg, [V(-0.2 + t * 0.1, 0.05, 0.1), V(0, -0.02, 0.15 - t * 0.1), V(0.2 - t * 0.1, 0.04, 0.08)], 0.012, ember);
    tail.push(seg);
  }
  // The tip: a fan of glass blades.
  const tipT = tail[N - 1]!;
  for (let i = -1; i <= 1; i++) blade(tipT, scaleM, V(0, 0, 0), V(i * 0.6, 0.25, fly ? -1 : 0.4), 0.42 - Math.abs(i) * 0.1, 0.07);

  /* ---------- wings of smoke ---------- */
  const smokeM = smoke('#0e0b13', 0.93, { side: THREE.DoubleSide, emissive: '#1c0a26' });
  const haze = smoke('#241a30', 0.36, { side: THREE.DoubleSide, emissive: '#2a0f3a' });
  const veinM = basic('#9b4dff', 0.55);
  const wispM = basic('#1a1420', 0.38);
  interface Wing { k: number; geos: { geo: THREE.BufferGeometry; rest: Float32Array; edge: [number, number][] }[]; wisps: Ribbon[]; puffs: THREE.Sprite[]; edgePts: THREE.Vector3[] }
  const wings: Wing[] = [];
  const wingBones: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const X = (v: THREE.Vector3): THREE.Vector3 => V(v.x * k, v.y, v.z);
    const sh = V(0, 0, 0), elbow = X(V(0.95, 0.35, -0.25)), wrist = X(V(1.75, 0.55, 0.05));
    // Three bones: the arm (its pivot is the wing's own frame, set at the old first frame of the beat), the
    // forearm at the elbow and the hand at the wrist, which carries the fingers and the fans between them.
    const side = k < 0 ? 'L' : 'R';
    const piv = joint(front, V(k * 0.6, 1.7, 0.42), `wing${side}`).p;
    if (fly) piv.rotation.set(0, k * 0.05, k * 0.62);
    else piv.rotation.set(-0.15, -k * 0.75, k * 0.82);
    const elbowJ = joint(piv, elbow, `elbow${side}`);
    const handJ = joint(elbowJ.abs, wrist, `hand${side}`);
    const fore = elbowJ.abs, hand = handJ.abs;
    wingBones[`wing${side}`] = piv;
    wingBones[`elbow${side}`] = elbowJ.p;
    wingBones[`hand${side}`] = handJ.p;
    const tips = [X(V(2.85, 0.95, 0.15)), X(V(2.75, 0.25, -0.7)), X(V(2.25, -0.15, -1.35)), X(V(1.45, -0.28, -1.65))];
    const root = X(V(0.05, -0.32, -1.45));
    // Bones of glass, with a violet vein of fire along each finger.
    limb(piv, sh, elbow, 0.1, 0.075, glass, 6);
    limb(fore, elbow, wrist, 0.075, 0.055, glass, 6);
    shard(fore, glass, elbow.x, elbow.y, elbow.z, 0.1);
    shard(hand, scaleM, wrist.x, wrist.y, wrist.z, 0.1);
    blade(hand, scaleM, wrist, X(V(0.2, 0.5, 0.6)), 0.3, 0.05);
    for (const tp of tips) {
      limb(hand, wrist, tp, 0.04, 0.012, glass, 4);
      M(new THREE.TubeGeometry(new THREE.LineCurve3(wrist.clone().lerp(tp, 0.08), wrist.clone().lerp(tp, 0.85)), 2, 0.012, 3), veinM, hand);
    }
    // Membranes: fans between the fingers whose trailing edges scallop in and boil every frame.
    const panels: [THREE.Vector3, THREE.Vector3, THREE.Vector3][] = [
      [wrist, tips[0]!, tips[1]!],
      [wrist, tips[1]!, tips[2]!],
      [wrist, tips[2]!, tips[3]!],
      [elbow, tips[3]!, root],
      [sh, elbow, root],
    ];
    // The fans between the fingers ride the hand; the one from the elbow, the forearm; the inner one, the arm.
    const panelBone = [hand, hand, hand, fore, piv];
    const geos: Wing['geos'] = [];
    const edgePts: THREE.Vector3[] = [];
    const S = 5;
    for (const [pi, [hub, a, b]] of panels.entries()) {
      // The edge sags toward the hub between the fingers (deepest midway).
      const E = Array.from({ length: S + 1 }, (_, j) => a.clone().lerp(b, j / S).lerp(hub, Math.sin((j / S) * Math.PI) * 0.22));
      const pos: number[] = [];
      for (let i = 0; i < S; i++) {
        const p0 = E[i]!, p1 = E[i + 1]!;
        pos.push(hub.x, hub.y, hub.z, p0.x, p0.y, p0.z, p1.x, p1.y, p1.z);
      }
      // Each inner edge point is shared by two triangles (p1 of one, p0 of the next): they move together.
      const edge: [number, number][] = [];
      for (let j = 1; j < S; j++) {
        edge.push([(j - 1) * 9 + 6, j * 9 + 3]);
        edgePts.push(E[j]!.clone());
      }
      const geo = new THREE.BufferGeometry();
      const arr = new Float32Array(pos);
      geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
      geo.computeVertexNormals();
      M(geo, smokeM, panelBone[pi]!);
      // A second, paler skin of haze a little behind, boiling on its own.
      const geo2 = geo.clone();
      M(geo2, haze, panelBone[pi]!).position.set(0, -0.04, -0.06);
      geos.push({ geo, rest: arr.slice(), edge }, { geo: geo2, rest: arr.slice(), edge });
    }
    // Smoke streaming off the trailing edge, and puffs that form and come apart.
    const wisps: Ribbon[] = [];
    for (let i = 0; i < 8; i++) {
      const e = Math.floor((i / 8) * edgePts.length);
      const p = edgePts[e]!;
      const pts = [p.clone(), p.clone().add(V(k * 0.1, 0.1, -0.3)), p.clone().add(V(k * 0.05, 0.22, -0.65)), p.clone().add(V(k * 0.15, 0.4, -0.95)), p.clone().add(V(k * 0.1, 0.6, -1.2))];
      // Each wisp rides the bone of the fan it streams off (four edge points per fan).
      wisps.push(new Ribbon(panelBone[Math.floor(e / 4)]!, pts, 0.022 + (i % 3) * 0.01, wispM, rng, 0.1));
    }
    // The edge of the smoke smoulders: sparks of ember and violet along it, coming and going.
    const puffs: THREE.Sprite[] = [];
    for (let i = 0; i < 10; i++) puffs.push(glowSprite(fore, i % 2 ? '#9b4dff' : '#ff6a1f', 0.22, V(0, 0, 0), 0.6, true));
    wings.push({ k, geos, wisps, puffs, edgePts });
  }

  /* ---------- embers rising off it ---------- */
  const embers: { s: THREE.Sprite; x: number; y: number; z: number; ph: number }[] = [];
  for (let i = 0; i < 12; i++) {
    const s = glowSprite(body, i % 3 ? '#ff7a2a' : '#b06bff', 0.14, V(0, 0, 0), 0.9, true);
    embers.push({ s, x: R() * 0.9, y: 0.9 + rng() * 0.6, z: R() * 2.2, ph: rng() });
  }
  const L = pointLight(c, 0xff6a2a, 1.6, 6, V(0, 1.2, 1.3));
  const L2 = pointLight(c, 0x9b4dff, 0.9, 5, V(0, 1.9, -0.2));

  // Boil only: fire, smoke, embers and light. The clips move the bones; nothing here touches them.
  const t = (n: number): void => {
    const f = ((n % DRAGON_FRAMES) + DRAGON_FRAMES) % DRAGON_FRAMES;
    const ph = (f / DRAGON_FRAMES) * Math.PI * 2;
    for (const tg of tongues) {
      const k = 0.75 + rng() * 0.6;
      tg.m.scale.set(0.85 + rng() * 0.3, k, 0.85 + rng() * 0.3);
      tg.m.position.y = (tg.h * k) / 2;
    }
    for (const w of wings) {
      // Boil: the trailing edges shift and tear a little every frame.
      for (const gg of w.geos) {
        const p = gg.geo.attributes.position as THREE.BufferAttribute;
        const arr = p.array as Float32Array;
        arr.set(gg.rest);
        for (const [i, j] of gg.edge) {
          const dx = R() * 0.14, dy = R() * 0.14, dz = R() * 0.2 - 0.05;
          for (const v of [i, j]) {
            arr[v] = (arr[v] as number) + dx;
            arr[v + 1] = (arr[v + 1] as number) + dy;
            arr[v + 2] = (arr[v + 2] as number) + dz;
          }
        }
        p.needsUpdate = true;
        gg.geo.computeVertexNormals();
      }
      for (const r of w.wisps) r.step();
      w.puffs.forEach((pf, i) => {
        const e = w.edgePts[(i * 3 + f) % w.edgePts.length]!;
        const life = ((f + i * 2) % 5) / 5;
        pf.position.set(e.x + R() * 0.1, e.y + life * 0.2, e.z - life * 0.3);
        pf.material.opacity = 0.7 * (1 - life);
      });
    }
    // The heart flares and the fire in the mouth smoulders.
    const glow = 0.45 + (Math.sin(ph * 2) + 1) * 0.15;
    heartGlow.material.opacity = glow;
    mouthGlow.material.opacity = 0.45 + (Math.sin(ph * 3) + 1) * 0.12;
    heart.scale.setScalar(1 + (f % 4 < 2 ? 0.08 : 0));
    L.intensity = (1.2 + glow) * LIGHT_K;
    L2.intensity = (0.7 + 0.2 * Math.cos(ph)) * LIGHT_K;
    for (const e of embers) {
      const p = (e.ph + f / DRAGON_FRAMES) % 1;
      e.s.position.set(e.x + Math.sin(p * 6 + e.ph * 9) * 0.08, e.y + p * 1.1, e.z);
      e.s.material.opacity = 0.9 * (1 - p);
    }
  };
  t(0);
  return done(c, fly ? 2.6 : 3.0, t, undefined, bonesOf(c, {
    body: trunkJ.p, chest: chestJ.p, hips: hipsJ.p,
    neck1: n1.p, neck2: n2.p, neck3: n3.p, head: headJ.p, jaw,
    tail1: tailJ[0]!.p, tail2: tailJ[1]!.p, tail3: tailJ[2]!.p, tail4: tailJ[3]!.p, tail5: tailJ[4]!.p,
    ...legBones, ...wingBones,
  }));
};
